// Helper function to check if error is network error
export function isNetworkError(message: string): boolean {
  return message.includes('Failed to fetch') || message.includes('ERR_NETWORK')
}

// Helper function to check if error is email confirmation error
export function isEmailConfirmationError(message: string): boolean {
  return message.includes('Email not confirmed') || message.includes('email_not_confirmed')
}

// Helper function to check if message contains invalid credentials
export function messageContainsInvalidCredentials(message: string): boolean {
  return message.includes('Invalid login credentials') || message.includes('Email not confirmed')
}

// Helper function to check if status is 400
export function isStatus400(status: number | undefined): boolean {
  return status === 400
}

// Helper function to check if error is invalid credentials
export function isInvalidCredentialsError(signInError: { message: string; status?: number }): boolean {
  return messageContainsInvalidCredentials(signInError.message) || isStatus400(signInError.status)
}

// Helper function to get network error message
export function getNetworkErrorMessage(): string {
  return 'Network error. Please check your internet connection and try again.'
}

// Helper function to get email confirmation error message
export function getEmailConfirmationErrorMessage(): string {
  return 'Please confirm your email address before signing in. Check your email for the confirmation link.'
}

// Helper function to get invalid credentials error message
export function getInvalidCredentialsErrorMessage(): string {
  return 'Invalid email or password. Please check your credentials and try again.'
}

// Helper function to get default error message
export function getDefaultErrorMessage(message: string): string {
  return message || 'An error occurred during sign in. Please try again.'
}

// Helper function to check password length
export function checkPasswordLength(pwd: string): { valid: boolean; error?: string } | null {
  if (pwd.length < 8) {
    return { valid: false, error: 'Password must be at least 8 characters long.' }
  }
  return null
}

// Helper function to check password uppercase
export function checkPasswordUppercase(pwd: string): { valid: boolean; error?: string } | null {
  if (!/[A-Z]/.test(pwd)) {
    return { valid: false, error: 'Password must contain at least one uppercase letter.' }
  }
  return null
}

// Helper function to check password lowercase
export function checkPasswordLowercase(pwd: string): { valid: boolean; error?: string } | null {
  if (!/[a-z]/.test(pwd)) {
    return { valid: false, error: 'Password must contain at least one lowercase letter.' }
  }
  return null
}

// Helper function to check password number
export function checkPasswordNumber(pwd: string): { valid: boolean; error?: string } | null {
  if (!/\d/.test(pwd)) {
    return { valid: false, error: 'Password must contain at least one number.' }
  }
  return null
}

// Helper function to check password special character
export function checkPasswordSpecialChar(pwd: string): { valid: boolean; error?: string } | null {
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)) {
    return { valid: false, error: 'Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>/?).' }
  }
  return null
}

// Helper function to validate password
export function validatePassword(pwd: string): { valid: boolean; error?: string } {
  const lengthCheck = checkPasswordLength(pwd)
  if (lengthCheck) return lengthCheck

  const uppercaseCheck = checkPasswordUppercase(pwd)
  if (uppercaseCheck) return uppercaseCheck

  const lowercaseCheck = checkPasswordLowercase(pwd)
  if (lowercaseCheck) return lowercaseCheck

  const numberCheck = checkPasswordNumber(pwd)
  if (numberCheck) return numberCheck

  const specialCharCheck = checkPasswordSpecialChar(pwd)
  if (specialCharCheck) return specialCharCheck

  return { valid: true }
}

// Helper function to check if error is user already registered
export function isUserAlreadyRegistered(errorMessage: string, status?: number): boolean {
  return errorMessage.includes('User already registered') || 
         errorMessage.includes('already registered') ||
         errorMessage.includes('already exists') ||
         status === 422
}

/** Supabase returns a user with empty identities when the email is already registered. */
export function isDuplicateSignupUser(user: { identities?: { length: number }[] | null } | null): boolean {
  return !!user && Array.isArray(user.identities) && user.identities.length === 0
}

// Helper function to get user already registered error message
export function getUserAlreadyRegisteredErrorMessage(): string {
  return 'An account with this email already exists. Please sign in instead. If you forgot your password, you can reset it.'
}

// Helper function to get network error message for signup
export function getNetworkErrorMessageForSignup(): string {
  return 'Network error. Please check your internet connection and try again.'
}

// Helper function to get default signup error message
export function getDefaultSignupErrorMessage(errorMessage: string): string {
  return errorMessage || 'An error occurred during sign up. Please try again.'
}
